// Fibonacci level calculations for XAU/USD
// Includes: ICT standard levels (OTE 0.618–0.786) + Sir's personal macro levels

// Standard ICT levels (for intraday OTE entries)
export const FIB_LEVELS_ICT = [
  { ratio: 0.000, label: '0.000', zone: 'swing', color: '#7ab3cc' },
  { ratio: 0.236, label: '0.236', zone: 'weak',  color: '#7ab3cc' },
  { ratio: 0.382, label: '0.382', zone: 'entry', color: '#ffd700' },
  { ratio: 0.500, label: '0.500', zone: 'mid',   color: '#7ab3cc' },
  { ratio: 0.618, label: '0.618 ⭐', zone: 'OTE', color: '#00ff88' },
  { ratio: 0.705, label: '0.705',    zone: 'OTE', color: '#00ff88' },
  { ratio: 0.786, label: '0.786 ⭐', zone: 'OTE', color: '#00ff88' },
  { ratio: 1.000, label: '1.000', zone: 'swing', color: '#7ab3cc' },
  { ratio: 1.272, label: '1.272', zone: 'ext',   color: '#00d4ff' },
  { ratio: 1.618, label: '1.618', zone: 'ext',   color: '#00d4ff' },
];

// Sir's complete personal level set (all 23 levels from TradingView settings — 2026-06-26)
// Retracement (0–1) + Extensions (>1 = TP targets)
// BUY zones: 0.15, 0.75 | SELL zones: 0.84, 0.90
export const FIB_LEVELS_MACRO = [
  // ── Retracement levels (0 to 1) ──────────────────────────────
  { ratio: 0.000,  label: '0.000',          zone: 'swing',  color: '#7ab3cc' },
  { ratio: 0.075,  label: '0.075',          zone: 'weak',   color: '#7ab3cc' },
  { ratio: 0.150,  label: '0.150 🟢 BUY',  zone: 'BUY',    color: '#00ff88' },
  { ratio: 0.295,  label: '0.295',          zone: 'weak',   color: '#7ab3cc' },
  { ratio: 0.382,  label: '0.382',          zone: 'entry',  color: '#ffd700' },
  { ratio: 0.400,  label: '0.400',          zone: 'weak',   color: '#7ab3cc' },
  { ratio: 0.500,  label: '0.500 EQ',       zone: 'mid',    color: '#7ab3cc' },
  { ratio: 0.600,  label: '0.600',          zone: 'weak',   color: '#7ab3cc' },
  { ratio: 0.750,  label: '0.750 🟢 BUY',  zone: 'BUY',    color: '#00ff88' },
  { ratio: 0.840,  label: '0.840 🔴 SELL', zone: 'SELL',   color: '#ff3333' },
  { ratio: 0.900,  label: '0.900 🔴 SELL', zone: 'SELL',   color: '#ff3333' },
  { ratio: 1.000,  label: '1.000',          zone: 'swing',  color: '#7ab3cc' },
  // ── Extension levels (TP targets beyond swing) ────────────────
  { ratio: 1.618,  label: '1.618 TP1',      zone: 'ext',    color: '#00d4ff' },
  { ratio: 1.900,  label: '1.900 TP2',      zone: 'ext',    color: '#00d4ff' },
  { ratio: 2.100,  label: '2.100 TP3',      zone: 'ext',    color: '#00d4ff' },
  { ratio: 2.400,  label: '2.400',          zone: 'ext',    color: '#7ab3cc' },
  { ratio: 2.618,  label: '2.618 TP',       zone: 'ext',    color: '#00d4ff' },
  { ratio: 2.800,  label: '2.800',          zone: 'ext',    color: '#7ab3cc' },
  { ratio: 3.000,  label: '3.000',          zone: 'ext',    color: '#7ab3cc' },
  { ratio: 3.100,  label: '3.100',          zone: 'ext',    color: '#7ab3cc' },
  { ratio: 3.300,  label: '3.300',          zone: 'ext',    color: '#7ab3cc' },
  { ratio: 4.000,  label: '4.000',          zone: 'ext',    color: '#00d4ff' },
  { ratio: 4.236,  label: '4.236 MACRO TP', zone: 'ext',    color: '#00d4ff' },
];

export const FIB_LEVELS = FIB_LEVELS_ICT;

export const calculateFibLevels = (swingHigh, swingLow, trend, mode = 'ict') => {
  const high = parseFloat(swingHigh);
  const low  = parseFloat(swingLow);
  if (isNaN(high) || isNaN(low) || high <= low) return null;
  const diff = high - low;

  const levels = mode === 'macro' ? FIB_LEVELS_MACRO : FIB_LEVELS_ICT;

  return levels.map(f => {
    const price = trend === 'up'
      ? low  + diff * f.ratio
      : high - diff * f.ratio;
    return { ...f, price: parseFloat(price.toFixed(2)) };
  }).sort((a, b) => trend === 'up' ? a.price - b.price : b.price - a.price);
};

export const getOTEZone = (levels, trend) => {
  if (!levels) return null;
  const l618 = levels.find(l => l.ratio === 0.618);
  const l786 = levels.find(l => l.ratio === 0.786);
  if (!l618 || !l786) return null;
  const prices = [l618.price, l786.price].sort((a, b) => a - b);
  return { bottom: prices[0], top: prices[1], mid: ((prices[0] + prices[1]) / 2).toFixed(2) };
};

// Sir's macro BUY zones (0.15 and 0.75)
export const getMacroBuyZones = (levels) => {
  if (!levels) return [];
  return levels.filter(l => l.zone === 'BUY');
};

// Sir's macro SELL zones (0.84 and 0.90)
export const getMacroSellZones = (levels) => {
  if (!levels) return [];
  return levels.filter(l => l.zone === 'SELL');
};

export const isInMacroBuyZone = (price, levels) => {
  if (!price || !levels) return false;
  const p = parseFloat(price);
  const buyZones = getMacroBuyZones(levels);
  return buyZones.some(z => Math.abs(p - z.price) <= 50); // within 50 USD of zone
};

export const isInMacroSellZone = (price, levels) => {
  if (!price || !levels) return false;
  const p = parseFloat(price);
  const sellZones = getMacroSellZones(levels);
  return sellZones.some(z => Math.abs(p - z.price) <= 50);
};

export const getPricePosition = (price, levels) => {
  if (!levels || !price) return null;
  const p = parseFloat(price);
  const sorted = [...levels].sort((a, b) => a.price - b.price);
  for (let i = 0; i < sorted.length - 1; i++) {
    if (p >= sorted[i].price && p <= sorted[i + 1].price) {
      const pct = ((p - sorted[i].price) / (sorted[i + 1].price - sorted[i].price) * 100).toFixed(0);
      return { between: [sorted[i], sorted[i + 1]], pct };
    }
  }
  return null;
};

export const isInOTE = (price, levels, trend) => {
  if (!levels || !price) return false;
  const ote = getOTEZone(levels, trend);
  if (!ote) return false;
  const p = parseFloat(price);
  return p >= ote.bottom && p <= ote.top;
};

export const calculateRR = (entry, sl, tp) => {
  const e = parseFloat(entry), s = parseFloat(sl), t = parseFloat(tp);
  if (isNaN(e) || isNaN(s) || isNaN(t)) return null;
  const risk   = Math.abs(e - s);
  const reward = Math.abs(t - e);
  if (risk === 0) return null;
  return {
    risk:   risk.toFixed(2),
    reward: reward.toFixed(2),
    ratio:  (reward / risk).toFixed(2),
    text:   `1:${(reward / risk).toFixed(1)}`,
    pips:   (risk * 10).toFixed(1),
  };
};
